import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';

import { api } from '@/lib/api';
import { useContent } from '@/lib/i18n';

/**
 * A persona's name in the operator's language, from its id — the twin of
 * `useModuleName` (`plan-preproduction` X8).
 *
 * `useModuleName` was written because six screens were printing a database key
 * at an operator who had been reading the name, in their own language, in that
 * screen's own module filter. **The persona half of that fix was never made.**
 * Results, QC review and Candidates all print `persona.hidden_graduate` as a
 * muted line under the candidate reference, on the same screens where the module
 * beside it resolves to "Motivations & Interests".
 *
 * Same shape and same reasoning throughout: the query key is `['personas']`, the
 * one every persona filter already uses, so react-query serves this from cache
 * rather than issuing a request per screen — and an unknown id renders as
 * itself, which is what a retired persona needs.
 */
export function usePersonaName(): (personaId: string | undefined | null) => string {
  const content = useContent();
  const personas = useQuery({ queryKey: ['personas'], queryFn: api.personas });

  return useCallback(
    (personaId) => {
      if (!personaId) return '';
      const found = (personas.data?.items ?? []).find((p) => p.personaId === personaId);
      return found ? content(found.name) : personaId;
    },
    [personas.data, content],
  );
}
